import React, { useState, useEffect } from 'react';
import {
  Button,
  CollectionPreferences,
  FormField,
  Header,
  Input,
  Pagination,
  SpaceBetween,
  Table} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import ChangeMode from './ChangeMode';
import ChangeStatus from './ChangeStatus';
import * as APIt from '../API';
import { useDispatch, useSelector } from 'react-redux';
import { Privileges } from 'src/utils/UserSitesUtils';
import {
  selectUserSelectedSitePrivs,
  selectUserPrefs,
  setUserPrefs } from 'src/stores/slices/userSlice';
import {
  Search,
  selectInputDevices,
  selectInputsSearch,
  selectReadersAlarmsDevices,
  selectReadersAlarmsSearch,
  selectReadersModesDevices,
  selectReadersModesSearch,
  selectSelectedInputs,
  selectSelectedReadersAlarms,
  selectSelectedReadersModes,
  setSelectedInputs, 
  setSelectedReadersAlarms,
  setSelectedReadersModes,
  updateInputMasked,
  updateReaderMasked,
  updateReaderMode,
  updateSelectedInputMask,
  updateSelectedReaderMasked,
  updateSelectedReaderMode} from '../stores/slices/devicesSlice';
import { ColumnDefinitions, PaginationLabels, TableEmptyState, TableNoMatchState } from './table-config';
import InputsFilter from './InputsFilter';
import MaskButtonBar from './MaskButtonBar';
import ReadersAlarmFilter from './ReadersAlarmFilter';
import ReadersModeFilter from './ReadersModeFilter';
import ModeButtonBar from './ModeButtonBar';
import { updateUserPrefs } from 'src/utils/UserPrefsUtils';
import { Modes, Statuses } from 'src/constants/Constants';
import { debug } from '../utils/commonUtils';

import '../styles/table-select.scss';
import { useTranslation } from 'react-i18next';

export type SingleSelectionEventType = {
  event: string,
  label: string,
  value: string,
}

export enum SingleSelectionEvents {
  inputMask = 'Input Mask',
  readerDoorForcedMask = 'Door Forced Open',
  readerDoorHeldMask = 'Door Held Open',
  readerMode = 'Reader Mode'
};

interface DevicesTableInterface {
  deviceType: string;
  loadingDevices: boolean;
  refreshDevicesCallback(): Promise<void>;
}

function DevicesTable(props: DevicesTableInterface) {
  debug(`DevicesTable(): props is ${JSON.stringify(props)}`);

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const userSitePrivs = useSelector(selectUserSelectedSitePrivs) as Privileges[] | undefined;
  const userPrefs = useSelector(selectUserPrefs);

  const getDevicesSelector = () => {
    switch(props.deviceType) {
      case 'inputs':
        return useSelector(selectInputDevices) as APIt.ChildDevice[];
      case 'readersAlarms':
        return useSelector(selectReadersAlarmsDevices) as APIt.ChildDevice[];
      case 'readersModes':
        return useSelector(selectReadersModesDevices) as APIt.ChildDevice[];
    }
  };
  const devicesFromStore = getDevicesSelector();

  const getSelectedDevicesSelector = () => {
    switch(props.deviceType) {
      case 'inputs':
        return useSelector(selectSelectedInputs) as APIt.ChildDevice[];
      case 'readersAlarms':
        return useSelector(selectSelectedReadersAlarms) as APIt.ChildDevice[];
      case 'readersModes':
        return useSelector(selectSelectedReadersModes) as APIt.ChildDevice[];
    }
  };
  const selectedDevicesFromStore = getSelectedDevicesSelector();

  const getSearchSelector = () => {
    switch(props.deviceType) {
      case 'inputs':
        return useSelector(selectInputsSearch) as Search;
      case 'readersAlarms':
        return useSelector(selectReadersAlarmsSearch) as Search;
      case 'readersModes':
        return useSelector(selectReadersModesSearch) as Search;
    }
  };
  const searchFromStore = getSearchSelector();

  const setSelectedDevicesInStore = (selectedDevices: APIt.ChildDevice[]) => {
    switch(props.deviceType) {
      case 'inputs':
        dispatch(setSelectedInputs(selectedDevices));
        break;
      case 'readersAlarms':
        dispatch(setSelectedReadersAlarms(selectedDevices));
        break;
      case 'readersModes':
        dispatch(setSelectedReadersModes(selectedDevices));
        break;
    }
  };

  const onSelectionChangeHandler = (detail: { selectedItems: React.SetStateAction<any[]> }) => {
    setSelectedDevicesInStore(detail.selectedItems as APIt.ChildDevice[]);
  };

  const setPreferences = async (details: any) => {
    debug(`setPreferences(): details is ${JSON.stringify(details)}`);
    setPageSize(details.pageSize);
    let savedSearches = [];
    let nodeName = '';
    let typeName = '';
    switch(props.deviceType) {
      case 'inputs':
        nodeName = 'inputs';
        typeName = 'InputsPrefs';
        savedSearches = userPrefs.inputs?.savedSearches || [];
        break;
      case 'readersAlarms':
        nodeName = 'readersAlarms';
        typeName = 'ReadersAlarmsPrefs';
        savedSearches = userPrefs.readersAlarms?.savedSearches || [];
        break;
      case 'readersModes':
        nodeName = 'readersModes';
        typeName = 'ReadersModesPrefs';
        savedSearches = userPrefs.readersModes?.savedSearches || [];
        break;
    }
    const newUserPrefs = await updateUserPrefs(
      {
        ...userPrefs,
        [nodeName]: {
          __typename: typeName,
          numRecordsPerPage: details.pageSize,
          emailAddresses: emailAddresses,
          savedSearches: [
            ...savedSearches
          ]
        }
      });
      if (newUserPrefs) dispatch(setUserPrefs(newUserPrefs));
  };

  const resetSelectedItems = () => {
    debug(`DevicesTable() resetSelectedItems()`);
    setSelectedDevicesInStore([]);
  };

  const getDevices = () => {
    debug(`DevicesTable() getDevices() props.deviceType is ${props.deviceType}`);
    let devices: APIt.ChildDevice[] = devicesFromStore?.slice() || [];
    debug(`DevicesTable() getDevices() devices.length is ${devices.length}`);
    if (devices.length > 0) {
      devices.sort((c, p) => (c.DeviceName && p.DeviceName && c.DeviceName < p.DeviceName) ? 1 : -1);
    }
    debug(`DevicesTable() getDevices() devices is ${JSON.stringify(devices)}`);
    return devices;
  };

  const [ singleSelection, setSingleSelection ] = useState<{ device: APIt.ChildDevice, selection: SingleSelectionEventType } | null>(null);
  const [ changeStatusVisible, setChangeStatusVisible ] = useState(false);
  const [ changeModeVisible, setChangeModeVisible ] = useState(false);

  const emailAddressesFromStore = (deviceType: string): string => {
    debug(`emailAddressesFromStore() deviceType is ${deviceType}`);
    let newEmailAddresses = '';
    try {
      switch(deviceType) {
        case 'inputs':
          newEmailAddresses = userPrefs.inputs.emailAddresses;
          break;
        case 'readersAlarms':
          newEmailAddresses = userPrefs.readersAlarms.emailAddresses;
          break;
        case 'readersModes':
          newEmailAddresses = userPrefs.readersModes.emailAddresses;
          break;
      }
      debug(`emailAddressesFromStore() newEmailAddresses is ${newEmailAddresses}`);
      return newEmailAddresses;
    } catch(error) {
      return '';
    }
  }

  const pageSizeFromStore = (deviceType: string): number => {
    debug(`pageSizeFromStore() deviceType is ${deviceType}`);
    let numRecs = 50;
    try {
      switch(deviceType) {
        case 'inputs':
          numRecs = userPrefs.inputs.numRecordsPerPage;
          break;
        case 'readersAlarms':
          numRecs = userPrefs.readersAlarms.numRecordsPerPage;
          break;
        case 'readersModes':
          numRecs = userPrefs.readersModes.numRecordsPerPage;
          break;
      }
      debug(`pageSizeFromStore() numRecs is ${numRecs}`);
      return numRecs;
    } catch(error) {
      return 50;
    }
  }

  const [ emailAddresses, setEmailAddresses ] = useState(emailAddressesFromStore(props.deviceType));
  const [ pageSize, setPageSize ] = useState<number>(pageSizeFromStore(props.deviceType));

  const [ selectDeselectButtonLabel, setSelectDeselectButtonLabel ] = useState<string>('Select All');

  const selectDeselectAll = () => {
    debug(`DevicesTable() selectDeselectAll() selectedDevicesFromStore.length is ${selectedDevicesFromStore?.length} devicesFromStore.length is ${devicesFromStore?.length} filterProps.filteringText is ${filterProps.filteringText}`);
    const filteredDevices = devicesFromStore?.filter(d => {
      if (!filterMatches(d)) return false;
      return d.DeviceName?.toUpperCase().includes(filterProps.filteringText.toUpperCase())
    });
    debug(`DevicesTable() selectDeselectAll() filteredDevices.length is ${filteredDevices?.length}`);
    if (selectDeselectButtonLabel === 'Deselect All') {
      setSelectedDevicesInStore([]);
      debug(`DevicesTable() selectDeselectAll() setting label to "Select All"`);
      setSelectDeselectButtonLabel('Select All');
    } else if (selectDeselectButtonLabel === 'Select All' && filteredDevices) {
      setSelectedDevicesInStore(filteredDevices);
      debug(`DevicesTable() selectDeselectAll() setting label to "Deselect All"`);
      setSelectDeselectButtonLabel('Deselect All');
    }
  };

  const [ selectDeselectAllDisabled, setSelectDeselectAllDisabled ] = useState<boolean>(false);

  const setInputMaskButtonHandler = (device: APIt.ChildDevice, selection: string) => {
    debug(`setInputMaskButtonHandler() device is ${JSON.stringify(device)} selection is ${selection}`);
    setSingleSelection({ device, selection: { event: SingleSelectionEvents.inputMask, label: selection, value: selection } });
    setChangeStatusVisible(true);
  };

  const setReaderDoorForcedMaskButtonHandler = (device: APIt.ChildDevice, selection: string) => {
    debug(`setReaderDoorForcedMaskButtonHandler() device is ${JSON.stringify(device)} selection is ${selection}`);
    setSingleSelection({ device, selection: { event: SingleSelectionEvents.readerDoorForcedMask, label: selection, value: selection } });
    setChangeStatusVisible(true);
  };

  const setReaderDoorHeldMaskButtonHandler = (device: APIt.ChildDevice, selection: string) => {
    debug(`setReaderDoorHeldMaskButtonHandler() device is ${JSON.stringify(device)} selection is ${selection}`);
    setSingleSelection({ device, selection: { event: SingleSelectionEvents.readerDoorHeldMask, label: selection, value: selection } });
    setChangeStatusVisible(true);
  };

  const setReaderModeButtonHandler = (device: APIt.ChildDevice, selection: string) => {
    debug(`setReaderModeButtonHandler() device is ${JSON.stringify(device)} selection is ${selection}`);
    setSingleSelection({ device, selection: { event: SingleSelectionEvents.readerMode, label: selection, value: selection } });
    setChangeModeVisible(true);
  };


  const filterMatches = (item: any) => {
    debug(`DevicesTable() filterMatches() props.deviceType is ${props.deviceType}`);
    if (!props.deviceType) return;
    switch(props.deviceType) {
      case 'inputs':
        if (props.deviceType !== 'inputs') return;
        debug(`DevicesTable() filterMatches() devicesFromStore.length is ${devicesFromStore?.length}`);
        if (searchFromStore?.maskStatus && searchFromStore.maskStatus.value && searchFromStore.maskStatus.value !== 'Any Status') {
          debug(`DevicesTable() filterMatches() searchFromStore.maskStatus is ${JSON.stringify(searchFromStore.maskStatus)}`);
          debug(`DevicesTable() filterMatches() item.deviceName is ${item.DeviceName} latestDevices.filter() item.Masked is ${item.Masked}`);
            switch(searchFromStore.maskStatus!.value) {
              case 'Unknown':
                return (item.Masked == undefined || item.Masked == null);
              case 'Masked':
                return (item.Masked);
              case 'Unmasked':
                return (item.Masked !== null && item.Masked !== undefined && !item.Masked);
              default:
                return true;
            };
        }
        return true;
      case 'readersAlarms':
        debug(`DevicesTable() filterMatches() devicesFromStore.length is ${devicesFromStore?.length}`);
        if (searchFromStore?.maskStatus && searchFromStore.maskStatus.value && searchFromStore.maskStatus.value !== 'Any Status') {
          debug(`DevicesTable() filterMatches() searchFromStore.maskStatus is ${JSON.stringify(searchFromStore.maskStatus)}`);
            debug(`DevicesTable() filterMatches() item.deviceName is ${item.DeviceName} item.DoorForcedMasked is ${JSON.stringify(item.DoorForcedMasked)}`);
            debug(`DevicesTable() filterMatches() item.deviceName is ${item.DeviceName} item.DoorHeldMasked is ${JSON.stringify(item.DoorHeldMasked)}`);
            switch(searchFromStore.maskStatus!.value) {
              case 'Unknown':
                return (((item.DoorForcedMasked == undefined || item.DoorForcedMasked == null) || (item.DoorHeldMasked == undefined || item.DoorHeldMasked == null)));
              case 'Masked':
                return (((item.DoorForcedMasked !== null && item.DoorForcedMasked) || (item.DoorHeldMasked !== null && item.DoorHeldMasked)));
              case 'Unmasked':
                return (
                  ((item.DoorForcedMasked !== null && item.DoorForcedMasked !== undefined && !item.DoorForcedMasked)
                  || (item.DoorHeldMasked !== null && item.DoorHeldMasked !== undefined && !item.DoorHeldMasked)));
              default:
                return true;
            };
        }
        return true;
      case 'readersModes':
        debug(`DevicesTable() filterMatches() devicesFromStore.length is ${devicesFromStore?.length}`);
        if (searchFromStore && Array.isArray(searchFromStore.modes) && searchFromStore.modes.length < Modes.length) {
          debug(`DevicesTable() filterMatches() searchFromStore.modes is ${JSON.stringify(searchFromStore.modes)}`);
          let modesValues: { value: string }[] = (searchFromStore.modes.map(m => { return { value: m.value } } ));
          let modes: string[] = [];
          for (let modesValue of modesValues) {
            debug(`DevicesTable() filterMatches() modesValue is ${JSON.stringify(modesValue)}`);
            modes.push(...Object.values(modesValue));
          }
          debug(`DevicesTable() filterMatches() modes is ${JSON.stringify(modes)}`);
          return modes.includes(item.Mode!);
        }
        return true;
    }
  };

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps, propertyFilterProps } = useCollection(
    devicesFromStore || [],
    {
      filtering: {
        empty: <TableEmptyState title='No Devices' />,
        noMatch: <TableNoMatchState onClearFilter={ () => actions.setFiltering('') } />,
        filteringFunction: (item: any, filteringText) => {
          if (!filterMatches(item)) {
            return false;
          }
          const filteringTextLowerCase = filteringText.toLowerCase();
          return ['DeviceName'].map(key => item[key]).some(
            value => typeof value === 'string' && value.toLowerCase().indexOf(filteringTextLowerCase) > -1
          );
        },
      },
      pagination: { pageSize: pageSize },
      sorting: {},
      selection: { trackBy: 'DeviceName' }
    }
  );

  const updateDevice = async (updatedDevice: APIt.ChildDevice) => {
    debug(`DevicesTable() updatedDevice() updatedDevice is ${JSON.stringify(updatedDevice)} props.deviceType is ${props.deviceType}`);
    if (!updatedDevice) return;
    switch(props.deviceType) {
      case 'inputs':
        dispatch(updateInputMasked(updatedDevice));
        dispatch(updateSelectedInputMask(updatedDevice));
        break;
      case 'readersAlarms':
        dispatch(updateReaderMasked(updatedDevice));
        dispatch(updateSelectedReaderMasked(updatedDevice));
        break;
      case 'readersModes':
        dispatch(updateReaderMode(updatedDevice));
        dispatch(updateSelectedReaderMode(updatedDevice));
        break;
    }
  };

  const onguardDevices = (): boolean => {
    debug(`DevicesTable() onguardDevices()`);
    const onguardDevicesFound = devicesFromStore?.find(d => d.devicesource === 'onguard') !== undefined;
    debug(`DevicesTable() onguardDevices() onguardDevicesFound is ${onguardDevicesFound}`);
    return onguardDevicesFound;
  };

  const visibleColumns = () => {
    switch(props.deviceType) {
      case 'inputs':
        return [ 'DeviceName', 'Masked', 'SetInputMask' ] 
      case 'readersAlarms':
        if (userSitePrivs && userSitePrivs.length > 0 && userSitePrivs.includes(Privileges.all) && !onguardDevices())
          return [ 'DeviceName', 'DoorForcedOpen', 'SetReaderDoorForcedMask', 'DoorHeldOpen', 'SetReaderDoorHeldMask' ]
        return [ 'DeviceName', 'DoorForcedOpen', 'DoorHeldOpen' ]
      case 'readersModes':
        if (userSitePrivs && userSitePrivs.length > 0 && userSitePrivs.includes(Privileges.all))
          return [ 'DeviceName', 'Mode', 'SetMode' ]
        return [ 'DeviceName', 'Mode' ]
      default:
        return [];
    }
  };

  useEffect(() => {
    debug(`DevicesTable() useEffect() [selectedDevicesFromStore, filterProps.filteringText] selectedDevicesFromStore.length is ${selectedDevicesFromStore?.length} devices.length is ${devicesFromStore?.length} filterProps.filteringText is ${filterProps.filteringText}`);
    const filteredDevices = devicesFromStore?.filter(d => {
      if (!filterMatches(d)) return false;
      return d.DeviceName?.toUpperCase().includes(filterProps.filteringText.toUpperCase())
    });
    if (!filteredDevices) return;
    debug(`DevicesTable() useEffect() [selectedDevicesFromStore] filteredDevices.length is ${filteredDevices.length}`);
    if (selectedDevicesFromStore && selectedDevicesFromStore.length > 0 && selectedDevicesFromStore.length >= filteredDevices.length) {
      setSelectDeselectButtonLabel('Deselect All');
    }
    if (selectedDevicesFromStore && selectedDevicesFromStore.length < filteredDevices.length) {
      setSelectDeselectButtonLabel('Select All');
    }
  }, [selectedDevicesFromStore, filterProps.filteringText]);

  useEffect(() => {
    debug(`DevicesTable() useEffect() [filterProps.filteringText] selectedDevicesFromStore.length is ${selectedDevicesFromStore?.length} devices.length is ${devicesFromStore?.length} filterProps.filteringText is ${filterProps.filteringText}`);
    const filteredDevices = devicesFromStore?.filter(d => {
      if (!filterMatches(d)) return false;
      return d.DeviceName?.toUpperCase().includes(filterProps.filteringText.toUpperCase())
    });
    if (!filteredDevices) return;
    debug(`DevicesTable() useEffect() [filterProps.filteringText] filteredDevices.length is ${filteredDevices.length}`);
    if (selectedDevicesFromStore && selectedDevicesFromStore.length > 0 && selectedDevicesFromStore.length > filteredDevices.length) {
      setSelectedDevicesInStore(filteredDevices);
    }
    if (filteredDevices.length === 0 && selectDeselectButtonLabel === 'Deselect All') setSelectDeselectButtonLabel('Select All');
  }, [filterProps.filteringText]);

  useEffect(() => {
    debug(`DevicesTable() useEffect() [items.length] items.length is ${items.length}`);
    if (items.length === 0) setSelectDeselectAllDisabled(true);
    if (items.length > 0) setSelectDeselectAllDisabled(false);
  }, [items.length]);

  useEffect(() => {
    debug(`DevicesTable() useEffect() [changeModeVisible]`);
    if (!changeModeVisible && singleSelection) setSingleSelection(null);
  }, [changeModeVisible]);

  useEffect(() => {
    debug(`DevicesTable() useEffect() [changeStatusVisible]`);
    if (!changeStatusVisible && singleSelection) setSingleSelection(null);
  }, [changeStatusVisible]);

  return(
    <>
    <Table
      {...collectionProps}
      trackBy='DeviceName'
      ariaLabels={{
        selectionGroupLabel: 'Devices selection',
        allItemsSelectionLabel: ({ selectedItems }) =>
          `${selectedItems.length} ${
            selectedItems.length === 1 ? 'item' : 'items'
          } selected`,
        itemSelectionLabel: ({ selectedItems }, item) => {
          const isItemSelected = selectedItems.filter(
            i => i.DeviceName === item.DeviceName
          ).length;
          return `${item.DeviceName} is ${
            isItemSelected ? '' : 'not'
          } selected`;
        }
      }}
      columnDefinitions={
        [
          ...ColumnDefinitions.slice(0,3),
          {
            id: 'SetInputMask',
            header: 'Set Mask',
            cell: (item: APIt.ChildDevice) =>
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  onClick={() => setInputMaskButtonHandler(item, 'Masked') }
                  disabled={(item.Masked !== null && item.Masked !== undefined && item.Masked) ? true : false }>
                  {t('Mask')}
                </Button>
                <Button
                  onClick={() => setInputMaskButtonHandler(item, 'Unmask') }
                  disabled={(item.Masked !== null && item.Masked !== undefined && !item.Masked) ? true : false }>
                  {t('Unmask')}
                </Button>
              </SpaceBetween>
          },
          ...ColumnDefinitions.slice(3,4),
          {
            id: 'SetReaderDoorForcedMask',
            header: 'Set Door Forced Open Mask',
            cell: (item: APIt.ChildDevice) =>
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  onClick={() => setReaderDoorForcedMaskButtonHandler(item, 'Masked') }
                  disabled={(item.DoorForcedMasked !== null && item.DoorForcedMasked !== undefined && item.DoorForcedMasked) ? true : false }>
                  Mask
                </Button>
                <Button
                  onClick={() => setReaderDoorForcedMaskButtonHandler(item, 'Unmask') }
                  disabled={(item.DoorForcedMasked !== null && item.DoorForcedMasked !== undefined && !item.DoorForcedMasked) ? true : false }>
                  Unmask
                </Button>
              </SpaceBetween>
          },
          ...ColumnDefinitions.slice(4,5),
          {
            id: 'SetReaderDoorHeldMask',
            header: 'Set Door Held Open Mask',
            cell: (item: APIt.ChildDevice) =>
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  onClick={() => setReaderDoorHeldMaskButtonHandler(item, 'Masked') }
                  disabled={(item.DoorHeldMasked !== null && item.DoorHeldMasked !== undefined && item.DoorHeldMasked) ? true : false }>
                  Mask
                </Button>
                <Button
                  onClick={() => setReaderDoorHeldMaskButtonHandler(item, 'Unmask') }
                  disabled={(item.DoorHeldMasked !== null && item.DoorHeldMasked !== undefined && !item.DoorHeldMasked) ? true : false }>
                  Unmask
                </Button>
              </SpaceBetween>
          },
          ...ColumnDefinitions.slice(5),
          {
            id: 'SetMode',
            header: 'Set Mode',
            cell: (item: APIt.ChildDevice) =>
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  onClick={() => setReaderModeButtonHandler(item, 'Card and Pin') }
                  disabled={(item.Mode === 'Card and Pin') ? true : false }>
                  Card and Pin
                </Button>
                <Button
                  onClick={() => setReaderModeButtonHandler(item, 'Card Only') }
                  disabled={(item.Mode === 'Card Only') ? true : false }>
                  Card Only
                </Button>
                <Button
                  onClick={() => setReaderModeButtonHandler(item, 'Locked') }
                  disabled={(item.Mode === 'Locked') ? true : false }>
                  Locked
                </Button>
                <Button
                  onClick={() => setReaderModeButtonHandler(item, 'Pin or Card') }
                  disabled={(item.Mode === 'Pin or Card') ? true : false }>
                  Pin or Card
                </Button>
                <Button
                  onClick={() => setReaderModeButtonHandler(item, 'Unlocked') }
                  disabled={(item.Mode === 'Unlocked') ? true : false }>
                  Unlocked
                </Button>
              </SpaceBetween>
          },
        ]
      }
      empty={ <TableEmptyState title={'No Devices'} /> }
      onSelectionChange={({ detail }) =>
        onSelectionChangeHandler(detail)
      }
      selectedItems={selectedDevicesFromStore}
      items={items}
      loadingText='Loading Devices...'
      loading={props.loadingDevices}
      selectionType='multi'
      visibleColumns={visibleColumns()}
      filter={
        <div className='input-container'>
          {(props.deviceType === 'inputs')
            ? <InputsFilter
                filterProps={filterProps}
                filteredItemsCount={filteredItemsCount}
                resetSelectedItemsCallback={resetSelectedItems}
                setFilterTextCallback={ (text: string) => actions.setFiltering(text) }
              />
            : (props.deviceType === 'readersAlarms')
              ? <ReadersAlarmFilter
                  filterProps={filterProps}
                  filteredItemsCount={filteredItemsCount}
                  filteringText={filterProps.filteringText}
                  readersType={props.deviceType}
                  resetSelectedItemsCallback={resetSelectedItems}
                  setFilterTextCallback={ (text: string) => actions.setFiltering(text) }
                />
              : <ReadersModeFilter
                  filterProps={filterProps}
                  filteredItemsCount={filteredItemsCount}
                  filteringText={filterProps.filteringText}
                  readersType={props.deviceType}
                  resetSelectedItemsCallback={resetSelectedItems}
                  setFilterTextCallback={ (text: string) => actions.setFiltering(text) }
                />}
        </div>
      }
      header={
        <>
        <Header
          counter={
            selectedDevicesFromStore?.length
              ? '(' + selectedDevicesFromStore.length + `/${devicesFromStore?.length})`
              : `(${devicesFromStore?.length || 0})`
          }
        >
          {props.deviceType === 'inputs' ? 'Inputs' : 'Readers'}
        </Header>
        {(props.deviceType === 'inputs' || props.deviceType === 'readersAlarms') &&
          <MaskButtonBar
            userSitePrivs={userSitePrivs}
            selectedItemsLength={selectedDevicesFromStore?.length}
            selectDeselectAllButtonLabel={selectDeselectButtonLabel}
            selectDeselectAllCallBack={selectDeselectAll}
            selectDeselectAllDisabled={selectDeselectAllDisabled}
            setChangeStatusVisibleCallback={setChangeStatusVisible}
            refreshDevicesCallback={props.refreshDevicesCallback}
          />
        }
        {(props.deviceType === 'readersModes') &&
          <ModeButtonBar
            userSitePrivs={userSitePrivs}
            selectedItemsLength={selectedDevicesFromStore?.length}
            selectDeselectAllButtonLabel={selectDeselectButtonLabel}
            selectDeselectAllCallBack={selectDeselectAll}
            selectDeselectAllDisabled={selectDeselectAllDisabled}
            setChangeModeVisibleCallback={setChangeModeVisible}
            refreshDevicesCallback={props.refreshDevicesCallback}
         />
        }
        </>
      }
      pagination={
        <Pagination
          {...paginationProps}
          ariaLabels={PaginationLabels}
        />
      }
      preferences={
        <CollectionPreferences
          onConfirm={({ detail }) => setPreferences(detail)}
          title='Preferences'
          confirmLabel='Confirm'
          cancelLabel='Cancel'
          preferences={{
            pageSize: pageSize,
            visibleContent: [
              'DeviceName'
            ]
          }}
          pageSizePreference={{
            title: 'Select page size',
            options: [
              { value: 5, label: '5 devices' },
              { value: 10, label: '10 devices' },
              { value: 15, label: '15 devices' },
              { value: 25, label: '25 devices' },
              { value: 50, label: '50 devices' },
              { value: 100, label: '100 devices' }
            ],
          }}
          customPreference={(customValue, setCustomValue) => (
            <FormField label='eMail Addresses'>
              <Input
                onChange={({ detail }) => setEmailAddresses(detail.value)}
                placeholder='email addresses'
                value={emailAddresses}
              />
            </FormField>
          )}
        />
      }
      resizableColumns={true}
      stickyHeader={true}
    />
    {changeStatusVisible
      && <ChangeStatus
          changeStatusVisible={changeStatusVisible}
          deviceType={props.deviceType}
          emailAddresses={props.deviceType === 'inputs' ? userPrefs.inputs.emailAddresses : userPrefs.readersAlarms.emailAddresses}
          setChangeStatusVisibleCallback={setChangeStatusVisible}
          selectedDevices={selectedDevicesFromStore}
          singleSelection={singleSelection}
          statuses={Statuses}
          totalDevicesCount={devicesFromStore?.length}
          updateDeviceCallback={updateDevice}
        />
    }
    {changeModeVisible
      && <ChangeMode
          changeModeVisible={changeModeVisible}
          emailAddresses={userPrefs.readersModes.emailAddresses}
          modes={Modes}
          setChangeModeVisibleCallback={setChangeModeVisible}
          selectedDevices={selectedDevicesFromStore}
          singleSelection={singleSelection}
          totalDevicesCount={devicesFromStore?.length}
          updateDeviceCallback={updateDevice}
        />
    }
    </>
  );

}

export default DevicesTable;